import rootScope from '@lib/rootScope';
import apiManagerProxy from '@lib/apiManagerProxy';
import getPeerTitle from '@components/wrappers/getPeerTitle';
import getPeerPhoto from '@appManagers/utils/peers/getPeerPhoto';
import getMediaFromMessage from '@appManagers/utils/messages/getMediaFromMessage';
import getPreviewURLFromBytes from '@helpers/bytes/getPreviewURLFromBytes';
import bytesToBase64 from '@helpers/bytes/bytesToBase64';
import readBlobAsDataURL from '@helpers/blob/readBlobAsDataURL';
import parseEntities from '@lib/richTextProcessor/parseEntities';
import type {MyMessage} from '@appManagers/appMessagesManager';
import type {Message, PhotoSize} from '@layer';
import type {VlinkMessageBody, VlinkReplyBody, VlinkReplyKind} from './types';

type VlinkMessageContent = Pick<VlinkMessageBody, 'kind' | 'text' | 'emoji' | 'duration' | 'size' | 'thumb' | 'waveform'>;

let started = false;
let msgRelay = true;

/**
 * 仅在 vlink-pc 容器内运行时启用（window.api 由 vlink-pc 的 preload 注入）。
 * 独立模块，只挂 rootScope 事件监听，不介入聊天渲染/打开路径。
 * 实际的 WebSocket 连接由 vlink-pc 主进程持有、在多账号间共用（见 vlink-pc 的 src/main/hub.ts），
 * 本模块只通过 IPC 上报消息、接收回复。
 */
export function initVlinkConnection(): void {
  if(started || !window.api) return;
  started = true;

  void window.api.getMsgRelay().then((enabled) => {
    msgRelay = enabled;
  });
  window.api.onMsgRelayChanged((enabled) => {
    msgRelay = enabled;
  });
  window.api.onReply((body) => {
    void handleIncoming(body);
  });

  if(rootScope.myId) {
    void reportAccountInfo(rootScope.myId);
  }

  rootScope.addEventListener('user_auth', ({id}) => {
    void reportAccountInfo(id);
  });

  rootScope.addEventListener('history_multiappend', (message) => {
    void onNewMessage(message);
  });
}

async function reportAccountInfo(peerId: PeerId): Promise<void> {
  const [name, avatarUrl] = await Promise.all([
    getPeerTitle({peerId, plainText: true, useManagers: true}),
    loadAvatarUrl(peerId)
  ]);
  await window.api!.reportAccountInfo({uid: String(peerId), name, avatarUrl});
}

async function loadAvatarUrl(peerId: PeerId): Promise<string | undefined> {
  const user = await rootScope.managers.appUsersManager.getUser(peerId.toUserId());
  const photo = getPeerPhoto(user);
  if(!photo) return undefined;

  // loadAvatar 返回的是 blob: URL，只在当前 tweb WebContentsView 的渲染进程内有效；
  // vlink-pc 会把它转发给侧边栏窗口这个完全独立的渲染进程用作 <img src>，
  // 跨进程解析 blob: URL 必然失败，所以要转成自包含的 data: URL 再上报
  const blobUrl = await apiManagerProxy.loadAvatar(peerId, photo, 'photo_small');
  if(!blobUrl) return undefined;

  const blob = await fetch(blobUrl).then((response) => response.blob());
  return readBlobAsDataURL(blob);
}

async function handleIncoming(body: VlinkReplyBody): Promise<void> {
  if(body.kind === 'text') {
    await rootScope.managers.appMessagesManager.sendText({
      peerId: body.peerId.toPeerId(),
      text: body.text ?? ''
    });
    return;
  }

  if(!body.url) return;
  await sendReplyMedia(body.peerId, body.kind, body.url, body.text);
}

/** 下载三方回复携带的媒体链接（在 vlink-pc 主进程发起，绕开渲染进程 CORS）并作为对应类型的媒体消息发送 */
async function sendReplyMedia(peerId: string, kind: Exclude<VlinkReplyKind, 'text'>, url: string, caption: string | undefined): Promise<void> {
  const media = await window.api!.downloadMedia(url);
  if(!media) return;

  await rootScope.managers.appMessagesManager.sendFile({
    peerId: peerId.toPeerId(),
    file: new Blob([media.data], {type: media.mimeType}),
    isMedia: true,
    isVoiceMessage: kind === 'voice',
    caption
  });
}

async function onNewMessage(message: MyMessage): Promise<void> {
  if(!msgRelay || message._ !== 'message') return;
  if(message.pFlags.out || !message.peerId.isUser()) return; // 忽略自己发出的消息，只转发私聊
  if(await rootScope.managers.appPeersManager.isBot(message.peerId)) return; // 排除机器人对话

  const name = await getPeerTitle({peerId: message.peerId, plainText: true, useManagers: true});

  void window.api!.sendMessage({
    peerId: String(message.peerId),
    messageId: message.mid,
    date: message.date,
    name,
    ...getMessageContent(message)
  });
}

function getMessageContent(message: Message.message): VlinkMessageContent {
  const media = getMediaFromMessage(message, true);
  if(!media) {
    return isOnlyEmojiText(message.message) ?
      {kind: 'emoji', text: message.message} :
      {kind: 'text', text: message.message};
  }

  if(media._ === 'photo') {
    return {kind: 'photo', text: message.message || undefined, thumb: getStrippedThumb(media.sizes)};
  }

  if(media.type === 'sticker') {
    return {kind: 'sticker', emoji: media.stickerEmojiRaw, thumb: getStrippedThumb(media.thumbs, true)};
  }
  if(media.type === 'gif') {
    return {kind: 'gif', thumb: getStrippedThumb(media.thumbs)};
  }
  if(media.type === 'voice') {
    const waveform = media.attributes.find((attribute) => attribute._ === 'documentAttributeAudio')?.waveform;
    return {
      kind: 'voice',
      duration: media.duration,
      size: media.size,
      waveform: waveform ? bytesToBase64(waveform) : undefined
    };
  }
  if(media.type === 'video' || media.type === 'round') {
    return {
      kind: 'video',
      text: message.message || undefined,
      duration: media.duration,
      size: media.size,
      thumb: getStrippedThumb(media.thumbs)
    };
  }

  return {kind: 'other', text: message.message || undefined};
}

/** 从消息自带的 sizes/thumbs 里取压缩缩略图，同步解码，不发起任何网络下载 */
function getStrippedThumb(sizes: PhotoSize[] | undefined, isSticker = false): string | undefined {
  const stripped = sizes?.find((size): size is PhotoSize.photoStrippedSize => size._ === 'photoStrippedSize');
  return stripped ? getPreviewURLFromBytes(stripped.bytes, isSticker) : undefined;
}

/** 去除空白后整条文本是否全部由 emoji 组成，与聊天气泡"大表情"渲染判断逻辑一致（bubbles.ts） */
function isOnlyEmojiText(text: string): boolean {
  if(!text) return false;
  const emojiLength = parseEntities(text)
  .filter((entity) => entity._ === 'messageEntityEmoji')
  .reduce((acc, entity) => acc + entity.length, 0);
  return emojiLength === text.replace(/\s/g, '').length;
}
